import { FormEvent, useEffect, useState, useRef } from "react";
import { toast } from "@/utils/appToast";
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/utils/api";
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const COOLDOWN_KEY = "emailResetCooldown";
const COOLDOWN_DURATION = 300; // 5 minutes

// Email validation function
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"email" | "phone">("email");
  const [emailSent, setEmailSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [token, setToken] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Restore cooldown from storage
  useEffect(() => {
    const stored = localStorage.getItem(COOLDOWN_KEY);
    if (stored) {
      const expiry = parseInt(stored, 10);
      const now = Math.floor(Date.now() / 1000);
      const remaining = expiry - now;
      if (remaining > 0) {
        setCooldown(remaining);
        setEmailSent(true);
      } else {
        localStorage.removeItem(COOLDOWN_KEY);
      }
    }
  }, []);

  const restoreEmailCooldown = () => {
    const stored = localStorage.getItem(COOLDOWN_KEY);
    if (stored) {
      const expiry = parseInt(stored, 10);
      const now = Math.floor(Date.now() / 1000);
      const remaining = expiry - now;
      if (remaining > 0) {
        setCooldown(remaining);
        setEmailSent(true);
      } else {
        localStorage.removeItem(COOLDOWN_KEY);
      }
    }
  };

  useEffect(() => {
    if (activeTab === "email") {
      restoreEmailCooldown();
    }
  }, [activeTab]);

  // Countdown logic
  useEffect(() => {
    if (!emailSent || cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          localStorage.removeItem(COOLDOWN_KEY);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [emailSent, cooldown]);

  useEffect(() => {
    setEmail("");
    setPhone("");
    setPhoneError("");
    setShowVerificationForm(false);
    setShowResetForm(false);

    if (activeTab === "phone") {
      setEmailSent(false);
      setCooldown(0);
    }

    if (activeTab === "email") {
      const stored = localStorage.getItem(COOLDOWN_KEY);
      if (stored) {
        const expiry = parseInt(stored, 10);
        const now = Math.floor(Date.now() / 1000);
        const remaining = expiry - now;
        if (remaining > 0) {
          setCooldown(remaining);
          setEmailSent(true);
        } else {
          localStorage.removeItem(COOLDOWN_KEY);
        }
      }
    }
  }, [activeTab]);

  const updateToken = (token: string) => {
    setToken(token);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const formData = new FormData(e.currentTarget);
      const value = formData.get(activeTab) as string;

      if (!value) {
        toast.error(`Please enter your ${activeTab === "email" ? "email" : "phone number"}`);
        setIsLoading(false);
        return;
      }

      if (activeTab === "email") {
        const emailInput = formData.get("email") as string;
        setEmail(emailInput);

        if (!emailInput || !validateEmail(emailInput)) {
          toast.error('Please enter a valid email address');
          setIsLoading(false);
          return;
        }

        const data = await api.forgotPassword({ type: "email", email: emailInput });
        toast.success("Se ha enviado un enlace de verificación por correo electrónico a su dirección de correo electrónico");
        setEmailSent(true);
        const expiry = Math.floor(Date.now() / 1000) + COOLDOWN_DURATION;
        localStorage.setItem(COOLDOWN_KEY, expiry.toString());
        setCooldown(COOLDOWN_DURATION);

      } else {
        if (!phone || phone.replace(/\D/g, '').length < 11) {
          toast.error("Por favor ingrese un número de teléfono válido.");
          setPhoneError('Por favor ingrese un número de teléfono válido.');
          setIsLoading(false);
          return;
        }

        const fullPhone = '+' + phone;
        const data = await api.forgotPassword({ type: "sms", phone: fullPhone });
        setPhone(fullPhone);
        setShowVerificationForm(true);
        toast.success("Se ha enviado un código de verificación a tu teléfono.");
      }

    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Unexpected error occurred. Please try again.";
      toast.error(`Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetClose = () => {
    navigate("/ingresar");
  };

  const handlePhoneClose = () => {
    console.log("phone form closed");
  };

  const handleVerificationSuccess = (phone: string) => {
    setToken(phone);
    setPhoneCode(phone);
    setShowVerificationForm(false);
    setShowResetForm(true);
  };

  const handleResendEmail = async () => {
    try {
      await api.forgotPassword({ type: "email", email });
      toast.success("Correo reenviado correctamente");
      const expiry = Math.floor(Date.now() / 1000) + COOLDOWN_DURATION;
      localStorage.setItem(COOLDOWN_KEY, expiry.toString());
      setCooldown(COOLDOWN_DURATION);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Error al reenviar el correo";
      toast.error(`Error: ${message}`);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f3",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "20px",
        paddingTop: "120px",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "24px",
          padding: isMobile ? "28px 24px" : "32px 40px",
          maxWidth: "500px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Heading */}
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <img
              src="/images/phone_symbol.svg"
              alt="Phone Symbol"
              style={{
                width: '50px',
                height: '50px',
                display: 'block'
              }}
            />
          </div>
          <h1
            style={{
              fontSize: isMobile ? "22px" : "26px",
              fontWeight: 600,
              color: "#1e293b",
              margin: 0,
              lineHeight: "1.3",
            }}
          >
            {showResetForm ? t('forgotPassword') :
              showVerificationForm ? (t('enterOTP') || "Enter OTP") :
                t('forgottenPassword')}
          </h1>
        </div>

        {!showResetForm && !showVerificationForm ? (
          <>
            {/* Tabs */}
            <div style={{ display: 'flex', marginBottom: '24px', gap: '8px' }}>
              {["email", "phone"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as "email" | "phone")}
                  style={{
                    flex: 1,
                    backgroundColor: activeTab === tab ? "#4285F4" : "#e5e7eb",
                    border: "none",
                    color: activeTab === tab ? "white" : "#64748b",
                    height: '45px',
                    fontSize: '16px',
                    borderRadius: "8px",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                >
                  {tab === "email" ? t('byEmail') : t('byPhone')}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {/* Email or Phone field */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ color: "#1e293b", display: "block", fontSize: "14px", marginBottom: "6px" }}>
                    {activeTab === "email" ? t('email') : t('phone')} <span style={{ color: "red" }}> *</span>
                  </label>
                  {activeTab === "phone" ? (
                    <PhoneInput
                      country={'us'}
                      countryCodeEditable={false}
                      value={phone}
                      onChange={value => {
                        setPhone(value);
                        setPhoneError('');
                      }}
                      inputProps={{
                        name: 'phone',
                        required: true,
                        style: { width: '100%', minHeight: '48px', fontSize: '15px' }
                      }}
                      inputStyle={{
                        width: '100%',
                        borderRadius: "8px",
                        minHeight: '48px',
                        fontSize: '15px',
                        border: "1px solid #e5e7eb"
                      }}
                      buttonStyle={{
                        borderRadius: "8px 0 0 8px"
                      }}
                      containerStyle={{
                        width: '100%'
                      }}
                      specialLabel=""
                      disableDropdown={false}
                      onlyCountries={['us']}
                      onBlur={() => {
                        if (phone && phone.replace(/\D/g, '').length < 11) setPhoneError('Por favor ingrese un número de teléfono válido.');
                      }}
                    />
                  ) : (
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('enterEmail')}
                      required
                      disabled={emailSent}
                      style={{
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                        width: "100%",
                        minHeight: "48px",
                        padding: "12px 16px",
                        fontSize: "15px",
                        boxSizing: "border-box",
                        backgroundColor: emailSent ? "#f3f4f6" : "white"
                      }}
                    />
                  )}
                </div>

                {!emailSent && (
                  <button
                    className="theme-btn btn-style-one"
                    type="submit"
                    disabled={isLoading}
                    style={{
                      backgroundColor: "#4285F4",
                      color: "white",
                      width: "100%",
                      padding: "14px 32px",
                      borderRadius: "12px",
                      border: "none",
                      fontSize: "16px",
                      fontWeight: 600,
                      cursor: isLoading ? "not-allowed" : "pointer",
                      marginBottom: "8px"
                    }}
                  >
                    {isLoading ? (
                      <div
                        className="spinner-border"
                        style={{ height: "15px", width: "15px" }}
                        role="status"
                      ></div>
                    ) : (
                      t('continue')
                    )}
                  </button>
                )}
              </div>
            </form>

            {emailSent && activeTab === "email" && (
              <div style={{ marginTop: "16px", textAlign: "center" }}>
                <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "12px" }}>{t('didNotReceiveEmail')}</p>
                <button
                  onClick={handleResendEmail}
                  disabled={cooldown > 0}
                  style={{
                    color: "#4285F4",
                    fontSize: "14px",
                    padding: 0,
                    textDecoration: "underline",
                    background: "none",
                    border: "none",
                    cursor: cooldown > 0 ? "not-allowed" : "pointer",
                    opacity: cooldown > 0 ? 0.5 : 1,
                    fontWeight: 500
                  }}
                >
                  {cooldown > 0
                    ? `${t('retryIn')} ${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, "0")}`
                    : t('tryAgain')}
                </button>
              </div>
            )}

            {/* Back to login */}
            <div style={{ textAlign: "center", marginTop: "16px" }}>
              <Link
                to="/ingresar"
                style={{
                  color: "#4285F4",
                  textDecoration: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                ← {t('backToLogin') || "Back to Login"}
              </Link>
            </div>
          </>
        ) : showResetForm ? (
          <ResetPasswordFormInline 
            email={email} 
            token={token} 
            phone={phone}
            onSuccess={() => navigate("/ingresar")}
            isMobile={isMobile}
            t={t}
          />
        ) : (
          <PhoneVerificationInline 
            phone={phone} 
            onVerificationSuccess={handleVerificationSuccess} 
            onBack={() => {
              setShowVerificationForm(false);
              setPhone("");
            }}
            isMobile={isMobile}
            t={t}
          />
        )}
      </div>
    </div>
  );
};

// Inline Reset Password Form Component
const ResetPasswordFormInline = ({ email, token,phone, onSuccess, isMobile, t }: { email: string; phone?: string, token?: string; onSuccess: () => void; isMobile: boolean; t: any }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const formData = new FormData(e.currentTarget);
      const newPassword = formData.get("password") as string;
      const confirmPassword = formData.get("confirmPassword") as string;

      if (newPassword.length < 6) {
        toast.error("La contraseña debe tener al menos 6 caracteres");
        setIsLoading(false);
        return;
      }

      if (newPassword !== confirmPassword) {
        toast.error("Las contraseñas no coinciden");
        setIsLoading(false);
        return;
      }

      const data = await api.resetPassword({
        type: "email",
        newPass: newPassword,
        passRecoveryCode: token,
        forcePasswordReset: false,
        userId: "",
        email:email,
        phone:phone
      });

      if (data.success) {
        toast.success("Contraseña restablecida con éxito");
        setTimeout(() => {
          onSuccess();
        }, 1000);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Error al restablecer la contraseña");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form method="post" onSubmit={handleSubmit}>
      <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <div className="form-group" style={{ marginBottom: 0, position: "relative" }}>
          <label style={{ color: "#1e293b", display: "block", fontSize: "14px", marginBottom: "6px" }}>
            {t('newPassword')} <span style={{ color: "red" }}> *</span>
          </label>
          <div style={{ position: "relative" }}>
            <input
              type={showNewPassword ? "text" : "password"}
              name="password"
              placeholder={t('enterNewPassword')}
              required
              style={{
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                width: "100%",
                minHeight: "48px",
                padding: "12px 45px 12px 16px",
                fontSize: "15px",
                boxSizing: "border-box"
              }}
            />
            <IconButton
              onClick={() => setShowNewPassword(prev => !prev)}
              style={{
                position: "absolute",
                right: 5,
                top: "50%",
                transform: "translateY(-50%)",
                padding: 0,
                color: "#64748b",
              }}
            >
              {showNewPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 0, position: "relative" }}>
          <label style={{ color: "#1e293b", display: "block", fontSize: "14px", marginBottom: "6px" }}>
            {t('confirmPassword')} <span style={{ color: "red" }}> *</span>
          </label>
          <div style={{ position: "relative" }}>
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder={t('confirmNewPassword')}
              required
              style={{
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                width: "100%",
                minHeight: "48px",
                padding: "12px 45px 12px 16px",
                fontSize: "15px",
                boxSizing: "border-box"
              }}
            />
            <IconButton
              onClick={() => setShowConfirmPassword(prev => !prev)}
              style={{
                position: "absolute",
                right: 5,
                top: "50%",
                transform: "translateY(-50%)",
                padding: 0,
                color: "#64748b",
              }}
            >
              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </div>
        </div>

        <button
          className="theme-btn btn-style-one"
          type="submit"
          disabled={isLoading}
          style={{
            backgroundColor: "#4285F4",
            color: "white",
            width: "100%",
            padding: "14px 32px",
            borderRadius: "12px",
            border: "none",
            fontSize: "16px",
            fontWeight: 600,
            cursor: isLoading ? "not-allowed" : "pointer",
            marginBottom: "8px"
          }}
        >
          {isLoading ? (
            <div
              className="spinner-border"
              style={{ height: "15px", width: "15px" }}
              role="status"
            ></div>
          ) : (
            t('resetPassword')
          )}
        </button>

        <div style={{ textAlign: "center", marginTop: "8px" }}>
          <Link
            to="/ingresar"
            style={{
              color: "#4285F4",
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            ← {t('backToLogin') || "Back to Login"}
          </Link>
        </div>
      </div>
    </form>
  );
};

// Inline Phone Verification Component
const PhoneVerificationInline = ({ phone, onVerificationSuccess, onBack, isMobile, t }: { phone: string; onVerificationSuccess: (code: string) => void; onBack: () => void; isMobile: boolean; t: any }) => {
  const [cooldown, setCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const COOLDOWN_KEY_PHONE = "phone_otp_cooldown";
  const COOLDOWN_DURATION_PHONE = 300;

  useEffect(() => {
    const storedExpiry = localStorage.getItem(COOLDOWN_KEY_PHONE);
    if (storedExpiry) {
      const expiry = parseInt(storedExpiry, 10);
      const remaining = expiry - Math.floor(Date.now() / 1000);
      if (remaining > 0) {
        setCooldown(remaining);
      } else {
        localStorage.removeItem(COOLDOWN_KEY_PHONE);
      }
    } else {
      const expiry = Math.floor(Date.now() / 1000) + COOLDOWN_DURATION_PHONE;
      localStorage.setItem(COOLDOWN_KEY_PHONE, expiry.toString());
      setCooldown(COOLDOWN_DURATION_PHONE);
    }
  }, []);

  useEffect(() => {
    if (cooldown === 0) return;
    const interval = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          localStorage.removeItem(COOLDOWN_KEY_PHONE);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  const handleResendOTP = async () => {
    try {
      setResendLoading(true);
      await api.forgotPassword({ type: "sms", phone });
      toast.success("OTP reenviado con éxito");
      const expiry = Math.floor(Date.now() / 1000) + COOLDOWN_DURATION_PHONE;
      localStorage.setItem(COOLDOWN_KEY_PHONE, expiry.toString());
      setCooldown(COOLDOWN_DURATION_PHONE);
    } catch (error) {
      toast.error("No se pudo reenviar el código. Inténtelo de nuevo.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(0, 1);
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const verificationCode = code.join('');
      const data = await api.forgotPasswordByPhone({ otp: verificationCode, phone });

      if (data.success) {
        toast.success("Código verificado con éxito");
        onVerificationSuccess(verificationCode);
      } else {
        toast.error(data.message || "Verificación fallida. Inténtelo de nuevo.");
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Ocurrió un error inesperado.";
      toast.error(`Error: ${msg}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <p style={{ color: "#64748b", fontSize: "14px" }}>
          {t('weHaveSentOTP') || "We have sent a OTP code on"} <br />
          <strong style={{ color: "#1e293b" }}>{phone}</strong>
        </p>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ color: "#1e293b", display: "block", fontSize: "14px", marginBottom: "12px", textAlign: "center" }}>
              {t('enterOTP') || "Enter OTP"}
            </label>
            <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginBottom: "20px" }}>
              {Array(6).fill(0).map((_, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  maxLength={1}
                  value={code[index]}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  style={{
                    width: isMobile ? '40px' : '50px',
                    height: isMobile ? '48px' : '55px',
                    textAlign: 'center',
                    fontSize: isMobile ? '18px' : '20px',
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    background: '#fff',
                    boxSizing: 'border-box',
                    fontWeight: 600,
                    color: "#1e293b"
                  }}
                />
              ))}
            </div>
          </div>

          <button
            className="theme-btn btn-style-one"
            type="submit"
            disabled={isLoading || code.some(digit => !digit)}
            style={{
              backgroundColor: "#4285F4",
              color: "white",
              width: "100%",
              padding: "14px 32px",
              borderRadius: "12px",
              border: "none",
              fontSize: "16px",
              fontWeight: 600,
              cursor: isLoading || code.some(digit => !digit) ? "not-allowed" : "pointer",
              marginBottom: "8px"
            }}
          >
            {isLoading ? (
              <div
                className="spinner-border"
                style={{ height: "15px", width: "15px" }}
                role="status"
              ></div>
            ) : (
              t('submit') || "Submit"
            )}
          </button>

          <div style={{ textAlign: "center", marginTop: "8px" }}>
            <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "8px" }}>
              {t('didNotReceiveCode') || "Didn't receive the code?"}
            </p>
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={cooldown > 0 || resendLoading}
              style={{
                color: "#4285F4",
                fontSize: "14px",
                padding: 0,
                textDecoration: "underline",
                background: "none",
                border: "none",
                cursor: cooldown > 0 || resendLoading ? "not-allowed" : "pointer",
                opacity: cooldown > 0 ? 0.5 : 1,
                fontWeight: 500
              }}
            >
              {cooldown > 0
                ? `${t('retryIn')} ${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, "0")}`
                : resendLoading
                  ? (t('resending') || "Resending...")
                  : t('tryAgain')}
            </button>
          </div>

          <div style={{ textAlign: "center", marginTop: "8px" }}>
            <button
              type="button"
              onClick={onBack}
              style={{
                color: "#4285F4",
                textDecoration: "none",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                background: "none",
                border: "none",
                padding: 0
              }}
            >
              ← {t('back') || "Back"}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default ForgotPasswordPage;
